import { Box, useMediaQuery, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import hetich from "../Images/partners/HetichLogo.png";
import royalTouch from "../Images/partners/Royal-Touch.jpg";
import vamaLog from "../Images/partners/VamaLogo.png";
import century from "../Images/partners/centuryPly.png";
import greenPly from "../Images/partners/greenPlylogo.jpeg";
import saintGobain from "../Images/partners/saintGobainLogo.png";
import havells from "../Images/partners/havelsLogo.png";
import merinoWool from "../Images/partners/merinoLogo.jpeg";
import johnSon from "../Images/partners/johnsonLogo.png";
import SectionHeading from "../Shared/SectionHeading";

export const PartnerSection = () => {
  const prefersReducedMotion = useReducedMotion();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const partnerImages = [
    { src: hetich, name: "Hettich" },
    { src: royalTouch, name: "Royal Touch" },
    { src: vamaLog, name: "Vama" },
    { src: greenPly, name: "Greenply" },
    { src: century, name: "Century" },
    { src: saintGobain, name: "Saint-Gobain" },
    { src: havells, name: "Havells" },
    { src: merinoWool, name: "Merino" },
    { src: johnSon, name: "Johnson" },
  ];

  return (
    <Box
      sx={{
        px: { xs: 1.2, sm: 1.8, md: 2.6 },
        py: { xs: 4.4, md: 5.4 },
      }}
    >
      <SectionHeading
        eyebrow="Material Partners"
        title="Trusted Brands We Build With"
        subtitle="We collaborate with reputed brands and suppliers to ensure every design is backed by dependable quality and finish standards."
        align="center"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "1360px",
          mx: "auto",
          borderRadius: { xs: "22px", md: "30px" },
          p: { xs: 1.6, sm: 2.1, md: 2.5 },
          background:
            "linear-gradient(152deg, rgba(255,255,255,0.86), rgba(255,247,243,0.72))",
          border: "1px solid rgba(128,51,65,0.13)",
          boxShadow: "0 16px 36px rgba(20,8,10,0.08)",
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              px: 0.2,
              overflow: "hidden",
              "& .swiper": {
                width: "100%",
                overflow: "hidden",
                paddingBottom: "20px",
              },
              "& .swiper-slide": {
                width: "100% !important",
                boxSizing: "border-box",
              },
              "& .swiper-pagination": {
                bottom: "0px !important",
              },
            }}
          >
            <Swiper
              modules={[Autoplay, Pagination]}
              autoplay={{ delay: 2400, disableOnInteraction: false }}
              pagination={{ clickable: true }}
              loop
              slidesPerView={1}
              spaceBetween={12}
            >
              {partnerImages.map((partner) => (
                <SwiperSlide key={partner.name}>
                  <Box
                    sx={{
                      borderRadius: "14px",
                      border: "1px solid rgba(128,51,65,0.14)",
                      background: "rgba(255,255,255,0.72)",
                      minHeight: 128,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.5,
                    }}
                  >
                    <Box
                      component="img"
                      src={partner.src}
                      alt={partner.name}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: "100%",
                        maxWidth: 190,
                        maxHeight: 88,
                        objectFit: "contain",
                        filter: "saturate(1.04)",
                      }}
                      onContextMenu={(event) => event.preventDefault()}
                    />
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: { sm: 1.4, md: 1.8 },
            }}
          >
            {partnerImages.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={
                  prefersReducedMotion ? false : { opacity: 0, y: 18, scale: 0.97 }
                }
                whileInView={
                  prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }
                }
                viewport={{ once: true, amount: 0.3 }}
                transition={
                  prefersReducedMotion
                    ? undefined
                    : {
                        duration: 0.5,
                        delay: index * 0.05,
                        ease: [0.22, 1, 0.36, 1],
                      }
                }
                whileHover={prefersReducedMotion ? undefined : { y: -4 }}
              >
                <Box
                  sx={{
                    height: "100%",
                    borderRadius: "14px",
                    border: "1px solid rgba(128,51,65,0.14)",
                    background: "rgba(255,255,255,0.68)",
                    minHeight: { sm: 126, md: 136 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: { sm: 1.6, md: 2 },
                    transition: "transform 220ms ease, box-shadow 220ms ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 10px 20px rgba(20,8,10,0.12)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={partner.src}
                    alt={partner.name}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: "100%",
                      maxWidth: { sm: 176, md: 218 },
                      maxHeight: { sm: 82, md: 92 },
                      objectFit: "contain",
                      filter: "saturate(1.04)",
                    }}
                    onContextMenu={(event) => event.preventDefault()}
                  />
                </Box>
              </motion.div>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
